import { DataChatRequest } from "../types.ts";
import {
  apiServerUrl,
  buildAuthHeaders,
  dispatchAuthErrorIfUnauthorized,
  messageForStatus,
  messageForThrownError,
  parseErrorMessage,
} from "./ApiService.ts";

export type AgentStreamEvent =
  | { type: "text_delta"; content: string }
  | { type: "text_full"; content: string }
  | { type: "tool_call"; tool_name: string; tool_call_id: string }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
  | { type: "tool_call_args"; tool_call_id: string; args: Record<string, any> }
  | { type: "tool_result"; tool_call_id: string; duration_ms: number | null }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
  | { type: "chart_component"; chart: any; tool_call_id: string }
  | { type: "done"; session_id: string; assistant_message_id: string | null; stored_data_keys: string[]; stored_data_previews: Record<string, Record<string, unknown>[]>; available_data_sources: string[] }
  | { type: "error"; message: string };

/**
 * Opens an SSE stream to /datastudio/chat/stream and calls onEvent for each parsed event.
 * Returns a cancel function that aborts the stream.
 */
export function streamChat(
  request: DataChatRequest,
  onEvent: (event: AgentStreamEvent) => void,
  onDone: () => void,
): () => void {
  const controller = new AbortController();

  (async () => {
    let response: Response;
    try {
      response = await fetch(`${apiServerUrl}/datastudio/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...buildAuthHeaders(),
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        onEvent({ type: "error", message: messageForThrownError(err) });
        onDone();
      }
      return;
    }

    if (!response.ok || !response.body) {
      dispatchAuthErrorIfUnauthorized(response.status);
      let message = messageForStatus(response.status);
      if (response.body) {
        try {
          message = await parseErrorMessage(response);
        } catch {
          // keep status fallback if the body can't be parsed
        }
      }
      onEvent({ type: "error", message });
      onDone();
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE lines are separated by "\n\n"
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const json = line.slice(5).trim();
          if (!json) continue;
          try {
            const event = JSON.parse(json) as AgentStreamEvent;
            onEvent(event);
            if (event.type === "done" || event.type === "error") {
              onDone();
              return;
            }
          } catch {
            // ignore malformed SSE frames
          }
        }
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        console.error("Chat stream read failed:", err);
        onEvent({ type: "error", message: "De verbinding met de chat is verbroken. Probeer het opnieuw." });
      }
    } finally {
      onDone();
      reader.releaseLock();
    }
  })();

  return () => controller.abort();
}
